import { on } from '@ember/modifier';
import { service } from '@ember/service';
import { t } from 'ember-intl';
import { VStack } from 'tio-ui/components/layout';
import Button from 'tio-reimagined/components/button';
import Card from 'tio-reimagined/components/card';
import Component from '@glimmer/component';
import FaqAccordion from 'tio-reimagined/components/faq-accordion';
import Heading from 'tio-reimagined/components/heading';
import PageHero from 'tio-reimagined/components/page-hero';
import {
  PhHandCoins,
  PhClipboardText,
  PhUploadSimple,
  PhTrendUp,
  PhBuildings,
  PhCheckCircle,
  PhFileText,
  PhListChecks,
  PhQuestion,
} from 'ember-phosphor-icons';
import SectionHeader from 'tio-reimagined/components/section-header';
import Text from 'tio-reimagined/components/text';
import transitionTo from 'ember-router-helpers/helpers/transition-to';
import type { AccordionItem } from 'tio-reimagined/components/faq-accordion';
import type { IntlService } from 'ember-intl';

class PslfBlankState extends Component {
  @service declare intl: IntlService;

  get faqItems(): AccordionItem[] {
    return [
      {
        question: this.intl.t('pslf.reimagined.faqs.what_is_pslf.question'),
        answer: this.intl.t('pslf.reimagined.faqs.what_is_pslf.answer'),
      },
      {
        question: this.intl.t('pslf.reimagined.faqs.eligibility_requirements.question'),
        answer: this.intl.t('pslf.reimagined.faqs.eligibility_requirements.answer'),
      },
      {
        question: this.intl.t('pslf.reimagined.faqs.qualifying_employers.question'),
        answer: this.intl.t('pslf.reimagined.faqs.qualifying_employers.answer'),
      },
      {
        question: this.intl.t('pslf.reimagined.faqs.how_to_apply.question'),
        answer: this.intl.t('pslf.reimagined.faqs.how_to_apply.answer'),
      },
    ];
  }

  <template>
    <VStack @collapsed={{true}}>
      {{! Hero Section }}
      <PageHero
        @gradientClass="bg-linear-to-br from-violet-50 to-indigo-50 dark:from-violet-950/50 dark:to-indigo-950/50"
        class="text-gray-900 dark:text-gray-100 [&_svg]:text-violet-500"
      >
        <:icon>
          <PhHandCoins @size={{28}} />
          <Heading @level={{1}} class="text-gray-900 dark:text-gray-100 text-2xl">
            {{t "pslf.reimagined.hero.heading"}}
          </Heading>
        </:icon>
        <:default>
          <Text class="text-gray-600 dark:text-gray-400 text-lg max-w-2xl">
            {{t "pslf.reimagined.hero.description"}}
          </Text>
          <div class="mt-4 flex items-center gap-3">
            <Button
              @intent="primary"
              {{on "click" (transitionTo "authenticated.pslf.dashboard.start-new")}}
            >
              {{t "pslf.reimagined.hero.get_started"}}
            </Button>
          </div>
        </:default>
      </PageHero>

      {{! Simple Steps Section }}
      <Card @padding="lg" @roundedXl={{true}}>
        <SectionHeader
          @icon={{PhListChecks}}
          @title={{t "pslf.reimagined.steps.heading"}}
          @iconClass="text-violet-600"
          @level={{2}}
        />
        <div class="space-y-6">
          <div class="flex items-start gap-4">
            <div
              class="shrink-0 w-10 h-10 rounded-full bg-violet-100 dark:bg-violet-900 flex items-center justify-center"
            >
              <PhClipboardText @size={{20}} class="text-violet-600 dark:text-violet-400" />
            </div>
            <div>
              <Text @weight="semibold" class="mb-1">
                {{t "pslf.reimagined.steps.submit_form.title"}}
              </Text>
              <Text @variant="muted">
                {{t "pslf.reimagined.steps.submit_form.description"}}
              </Text>
            </div>
          </div>
          <div class="flex items-start gap-4">
            <div
              class="shrink-0 w-10 h-10 rounded-full bg-teal-100 dark:bg-teal-900 flex items-center justify-center"
            >
              <PhUploadSimple @size={{20}} class="text-teal-600 dark:text-teal-400" />
            </div>
            <div>
              <Text @weight="semibold" class="mb-1">
                {{t "pslf.reimagined.steps.upload_data.title"}}
              </Text>
              <Text @variant="muted">
                {{t "pslf.reimagined.steps.upload_data.description"}}
              </Text>
            </div>
          </div>
          <div class="flex items-start gap-4">
            <div
              class="shrink-0 w-10 h-10 rounded-full bg-indigo-100 dark:bg-indigo-900 flex items-center justify-center"
            >
              <PhTrendUp @size={{20}} class="text-indigo-600 dark:text-indigo-400" />
            </div>
            <div>
              <Text @weight="semibold" class="mb-1">
                {{t "pslf.reimagined.steps.track_progress.title"}}
              </Text>
              <Text @variant="muted">
                {{t "pslf.reimagined.steps.track_progress.description"}}
              </Text>
            </div>
          </div>
        </div>
      </Card>

      {{! Eligibility Section }}
      <Card @padding="lg" @roundedXl={{true}}>
        <SectionHeader
          @icon={{PhCheckCircle}}
          @title={{t "pslf.reimagined.eligibility.heading"}}
          @iconClass="text-teal-600"
          @level={{2}}
        />
        <div class="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div class="text-center">
            <div
              class="w-12 h-12 rounded-full bg-blue-100 dark:bg-blue-900 flex items-center justify-center mx-auto mb-3"
            >
              <PhBuildings @size={{24}} class="text-blue-600 dark:text-blue-400" />
            </div>
            <Text @weight="semibold" class="mb-1">
              {{t "pslf.reimagined.eligibility.qualifying_employer.title"}}
            </Text>
            <Text @variant="muted" @size="sm">
              {{t "pslf.reimagined.eligibility.qualifying_employer.description"}}
            </Text>
          </div>
          <div class="text-center">
            <div
              class="w-12 h-12 rounded-full bg-teal-100 dark:bg-teal-900 flex items-center justify-center mx-auto mb-3"
            >
              <PhCheckCircle @size={{24}} class="text-teal-600 dark:text-teal-400" />
            </div>
            <Text @weight="semibold" class="mb-1">
              {{t "pslf.reimagined.eligibility.payments.title"}}
            </Text>
            <Text @variant="muted" @size="sm">
              {{t "pslf.reimagined.eligibility.payments.description"}}
            </Text>
          </div>
          <div class="text-center">
            <div
              class="w-12 h-12 rounded-full bg-orange-100 dark:bg-orange-900 flex items-center justify-center mx-auto mb-3"
            >
              <PhFileText @size={{24}} class="text-orange-600 dark:text-orange-400" />
            </div>
            <Text @weight="semibold" class="mb-1">
              {{t "pslf.reimagined.eligibility.direct_loans.title"}}
            </Text>
            <Text @variant="muted" @size="sm">
              {{t "pslf.reimagined.eligibility.direct_loans.description"}}
            </Text>
          </div>
        </div>
      </Card>

      {{! FAQ Section }}
      <Card @padding="lg" @roundedXl={{true}}>
        <SectionHeader
          @icon={{PhQuestion}}
          @title={{t "pslf.reimagined.faqs.heading"}}
          @iconClass="text-amber-600"
          @level={{2}}
        />
        <FaqAccordion @items={{this.faqItems}} />
      </Card>
    </VStack>
  </template>
}

export default PslfBlankState;
